// Crayo, /create (upload flow) and /preview/:sessionId
//
// OVERNIGHT MIGRATION NOTE (2026-04-24):
// This file previously kept a mock in-memory session store. The Checkout
// team replaced that with real calls to the Forge backend via
// `src/api.js`. The rest of the file intentionally keeps the same
// React structure and window-globals convention so diffs stay small.
//
// WHY we still expose `priceForPages` + `formatPrice` on window:
// Payment.jsx and other legacy modules pull them from window. When Forge
// returns authoritative pricing from Stripe, these helpers become a
// display-only fallback (what to show BEFORE the session exists), not
// the source of truth for charged amounts.
import React from 'react';

const { Icon, Page, navigate, useToast } = window;

// Pulled off the module imported by src/main.jsx before this file runs.
// Components still author as globals for the pre-Vite pattern; the api
// helpers are the one exception we reach for through a real import below.
import {
  createSession,
  uploadPhotos,
  startGeneration,
  getSession,
  startCheckout,
  ApiError,
} from '../src/api.js';

const { useState: useStateC, useEffect: useEffectC, useRef: useRefC } = React;

// Price display helpers. Used for UI hints only; the server is the
// source of truth for charges (Stripe Price lookup_keys, see
// .planning/API.md §"Checkout").
function priceForPages(n, withCover = false) {
  let base;
  if (n <= 3) base = 4.90;
  else if (n <= 10) base = 12.90;
  else base = 22.90;
  if (withCover) base += 9.90;
  return base;
}
function formatPrice(n) {
  return n.toFixed(2).replace('.', ',') + ' RON';
}

// --- /create ---
function CreatePage() {
  // Flat-price offer: one PDF coloring book, up to MAX_PHOTOS pages, 9,90 lei.
  // The prior tier picker (3/10/20 pages) is gone, pre-upload price commitment
  // was the largest friction point in the funnel. MAX_PHOTOS is the upload cap.
  // Submit requires MIN_PHOTOS so users do not fall into the single-photo trap
  // (uploading 1 photo, seeing a clean free preview, and bouncing). Backend
  // session.tier still set to MAX_PHOTOS for backwards compat with the existing
  // Zod schema (3|10|20 union); 20 is a valid tier value.
  // Minimum photos required to submit. Prevents the single-photo conversion
  // trap where the user uploads one photo, sees a clean free preview, and
  // perceives no reason to pay. See .planning/pricing-pivot-2026-05-04.md.
  const MIN_PHOTOS = 3;
  // Hard cap on photos per session. Backend `sessions.ts` MAX_PHOTOS must
  // match. The value 20 is also a valid `tier` per the Zod TierSchema
  // ({3,10,20}), so `createSession(MAX_PHOTOS)` continues to validate
  // server-side without any schema change.
  const MAX_PHOTOS = 20;
  const [photos, setPhotos] = useStateC([]);
  const [dragOver, setDragOver] = useStateC(false);
  const [submitting, setSubmitting] = useStateC(false);
  const fileRef = useRefC();
  const cameraRef = useRefC();
  const toast = useToast();

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    // HEIC detection is best-effort on the client (MIME is often empty for
    // HEIC). Server is the source of truth — it sniffs magic bytes and
    // rejects regardless of extension or claimed Content-Type.
    const heic = files.filter((f) => /\.(heic|heif)$/i.test(f.name) || /image\/heic|heif/i.test(f.type));
    if (heic.length > 0) {
      toast.push('Fișierul pare HEIC. Te rugăm să salvezi ca JPG sau PNG înainte de încărcare.');
    }
    const formatOk = files.filter(
      (f) => /image\/(jpeg|png|webp)/i.test(f.type) || /\.(jpe?g|png|webp)$/i.test(f.name)
    );
    if (formatOk.length === 0 && files.length > 0 && heic.length === 0) {
      toast.push('Formatul nu este acceptat. Folosește JPG, PNG sau WebP.');
      return;
    }
    // Match the server-side cap (backend MAX_FILE_BYTES is 10 MB). Without
    // this check the user uploads up to 15 MB before getting a server error,
    // wasting their time and bandwidth.
    const MAX_BYTES = 10 * 1024 * 1024;
    const tooLarge = formatOk.filter((f) => f.size > MAX_BYTES);
    if (tooLarge.length > 0) {
      toast.push('Fișierul depășește 10 MB. Te rugăm să comprimi poza înainte de încărcare.');
    }
    const accepted = formatOk.filter((f) => f.size <= MAX_BYTES);
    if (accepted.length === 0) return;

    setPhotos((prev) => {
      const remaining = Math.max(0, MAX_PHOTOS - prev.length);
      const next = accepted.slice(0, remaining).map((f, i) => ({
        // Client-side id for UI key; server assigns the real photo id after upload.
        clientId: Math.random().toString(36).slice(2),
        file: f,
        name: f.name,
        url: URL.createObjectURL(f),
        size: f.size,
        seed: prev.length + i,
      }));
      return [...prev, ...next].slice(0, MAX_PHOTOS);
    });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const remove = (id) => setPhotos((p) => {
    const gone = p.find((x) => x.clientId === id);
    if (gone) URL.revokeObjectURL(gone.url);
    return p.filter((x) => x.clientId !== id);
  });

  const slotsLeft = MAX_PHOTOS - photos.length;
  const allSlotsFilled = photos.length >= MAX_PHOTOS;
  // Submit enabled once the user has at least MIN_PHOTOS uploaded. Consent is
  // given implicitly by clicking the Generează button (click-through pattern
  // under the button, see JSX below).
  const ready = photos.length >= MIN_PHOTOS && !submitting;

  // Real API flow: create session, upload photos, kick off generation,
  // navigate to the polling page. Errors surface as toasts; on any
  // failure we stay on /create so the user can retry without losing
  // their photos.
  const submit = async () => {
    if (!ready) return;
    setSubmitting(true);
    try {
      // Backend Zod TierSchema is still 3|10|20; pass MAX_PHOTOS (20 is a
      // valid tier value).
      const { sid } = await createSession(MAX_PHOTOS);
      if (!sid) throw new ApiError('session.bad_response', 'Răspuns neașteptat de la server. Reîncearcă.');

      // Upload all photos in one multipart request. API.md §"POST /api/session/:sid/photos"
      // accepts a repeatable `photos` field; sending them together means
      // the server sees a single tier-bounded check.
      await uploadPhotos(sid, photos.map((p) => p.file));

      // Kick off generation for all `pending` photos. Idempotent per API.md.
      await startGeneration(sid);

      navigate('/preview/' + sid);
    } catch (err) {
      // Known Romanian message in the error envelope (preferred).
      // Fallback: generic network copy already set by api.js.
      const msg = err && err.message ? err.message : 'Nu am reușit să pornim generarea. Reîncearcă.';
      toast.push(msg);
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <section style={{ paddingTop: 48 }}>
        <div className="container-narrow">
          <a href="#/" onClick={(e)=>{e.preventDefault();navigate('/');}} style={{ fontSize: 13, color: 'var(--muted)', display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 24 }}>
            <span style={{ transform: 'rotate(180deg)', display: 'inline-flex' }}><Icon.Arrow size={14} /></span> Înapoi
          </a>
          <h1 style={{ marginBottom: 16 }}>Încarcă pozele copilului tău</h1>
          <p style={{ fontSize: 17, color: 'var(--muted)', marginBottom: 32, maxWidth: 560 }}>
            Primești previzualizarea gratuit. Plătești doar 9,90 lei <span style={{ color: 'var(--muted)' }}>(în loc de <span style={{ textDecoration: 'line-through' }}>19,90</span>, -50%)</span> dacă îți place rezultatul.
            <br />
            <strong>Minim {MIN_PHOTOS} poze</strong> pentru o carte de colorat. Alege-le pe cele mai dragi.
          </p>

          {/* Photo counter pill (subtle, only shows when there are uploads). */}
          {photos.length > 0 && (
            <div style={{ display: 'inline-block', marginBottom: 16, padding: '6px 14px', borderRadius: 999, background: 'rgba(198,93,59,0.08)', color: 'var(--terracotta)', fontSize: 13, fontWeight: 500 }}>
              {photos.length} / {MAX_PHOTOS} {photos.length === 1 ? 'poză încărcată' : photos.length >= 20 ? 'de poze încărcate' : 'poze încărcate'}
            </div>
          )}

          {/* Upload zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); if (!allSlotsFilled) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { if (!allSlotsFilled) onDrop(e); else e.preventDefault(); }}
            onClick={() => { if (!allSlotsFilled) fileRef.current?.click(); }}
            role="button"
            tabIndex={0}
            aria-disabled={allSlotsFilled}
            onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !allSlotsFilled) fileRef.current?.click(); }}
            style={{
              border: '2px dashed ' + (allSlotsFilled ? 'var(--soft)' : (dragOver ? 'var(--terracotta)' : 'rgba(198,93,59,0.5)')),
              background: allSlotsFilled ? 'var(--soft)' : (dragOver ? 'rgba(198,93,59,0.06)' : 'var(--paper)'),
              borderRadius: 20,
              padding: '48px 24px',
              textAlign: 'center',
              cursor: allSlotsFilled ? 'not-allowed' : 'pointer',
              opacity: allSlotsFilled ? 0.6 : 1,
              transition: 'border-color 180ms ease, background 180ms ease',
              marginBottom: 16,
            }}>
            <div style={{ display: 'inline-flex', padding: 14, background: 'rgba(198,93,59,0.08)', borderRadius: '50%', marginBottom: 16 }}>
              <Icon.Upload />
            </div>
            <div style={{ fontFamily: 'Fraunces, serif', fontSize: 22, fontWeight: 500, marginBottom: 6 }}>
              {allSlotsFilled
                ? 'Ai încărcat numărul maxim de poze'
                : (
                  <>
                    {/* Drag-and-drop is desktop-only; the responsive
                        spans swap copy below 600px so mobile users
                        don't see "trage" (drag) for an action they
                        can't perform. The explicit "Încarcă din
                        dispozitiv" button below removes the need for
                        a "sau apasă pentru a selecta" hint. */}
                    <span className="crayo-upload-desktop">Trage pozele aici</span>
                    <span className="crayo-upload-mobile">Adaugă pozele tale</span>
                  </>
                )}
            </div>
            {/* Explicit CTA button, present at all viewports. Removes
                ambiguity about what's clickable inside the dashed zone.
                stopPropagation keeps the parent zone's onClick from also
                firing fileRef.click() (which would attempt to re-open
                the picker mid-tick). */}
            {!allSlotsFilled && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                style={{ marginTop: 18, marginBottom: 14 }}
              >
                <Icon.Upload size={16} /> Încarcă din dispozitiv
              </button>
            )}
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
              JPG, PNG, WebP · max 10 MB per poză · {slotsLeft > 0 ? ('încă ' + slotsLeft + ' ' + (slotsLeft === 1 ? 'poză disponibilă' : slotsLeft >= 20 ? 'de poze disponibile' : 'poze disponibile')) : 'maxim atins'}
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden
              onChange={(e) => addFiles(e.target.files)} />
          </div>

          {/* Mobile camera shortcut. The `capture="environment"` attribute
              opens the rear camera directly on iOS Safari + Android Chrome.
              Desktop browsers ignore `capture` and fall back to the file
              picker — which is identical to the upload zone above and
              therefore confusing UX. We hide the button entirely on
              ≥600px viewports via the media query in the <style> block
              at the bottom of this component. */}
          {!allSlotsFilled && (
            <div className="crayo-camera-mobile" style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => cameraRef.current?.click()}
                style={{ padding: '10px 18px', fontSize: 14 }}
              >
                📷 Fă o poză acum
              </button>
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden
                onChange={(e) => addFiles(e.target.files)} />
            </div>
          )}

          {/* Thumbs */}
          {photos.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
                <div style={{ fontSize: 14, color: 'var(--muted)' }}>Pozele tale</div>
                {!allSlotsFilled && (
                  <button className="btn btn-ghost" style={{ padding: '8px 14px', fontSize: 13 }} onClick={() => fileRef.current?.click()}>
                    <Icon.Plus size={14} /> Mai adaugă
                  </button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 12, marginBottom: 40 }}>
                {photos.map((p) => (
                  <div key={p.clientId} className="fade-in" style={{ position: 'relative', aspectRatio: '1', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--soft)', transition: 'transform 180ms ease' }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                    <img src={p.url} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <button aria-label="Șterge" onClick={() => remove(p.clientId)}
                      style={{ position: 'absolute', top: 6, right: 6, width: 26, height: 26, borderRadius: '50%', border: 'none', background: 'rgba(26,26,26,0.75)', color: 'var(--paper)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
                      <Icon.Close size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {photos.length === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, color: 'var(--muted)', fontSize: 14, padding: '4px 0 40px' }}>
              <Icon.Star size={20} />
              <span>Sfat: pozele făcute la lumină naturală, cu subiectul în prim-plan, dau cele mai bune rezultate.</span>
            </div>
          )}

          <button className="btn btn-primary btn-lg btn-full" disabled={!ready} onClick={submit}>
            {submitting
              ? 'Trimitem pozele…'
              : photos.length === 0
                ? `Urcă minim ${MIN_PHOTOS} poze pentru a continua`
                : photos.length < MIN_PHOTOS
                  ? `Mai urcă ${MIN_PHOTOS - photos.length} ${MIN_PHOTOS - photos.length === 1 ? 'poză' : 'poze'}`
                  : (<>Generează cartea ta gratuit <Icon.Arrow /></>)}
          </button>
          {/* Click-through consent. By pressing the button above the user
              implicitly agrees to the Terms and confirms photo rights. EU-
              legal for this kind of action and dramatically lower friction
              than two checkboxes. */}
          <p style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', marginTop: 14, lineHeight: 1.5 }}>
            Apăsând <strong>Generează</strong>, ești de acord cu{' '}
            <a href="#/termeni" onClick={(e)=>{e.preventDefault();navigate('/termeni');}} className="link">Termenii</a>{' '}
            și confirmi că ai dreptul să folosești pozele (inclusiv cele cu persoane).
          </p>
          <p style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', marginTop: 16 }}>
            Nu ți se cere niciun card până vezi rezultatul.
          </p>
          <p style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'center', marginTop: 6 }}>
            Dacă nu îți place rezultatul, îți returnăm banii, fără întrebări.
          </p>
        </div>
        <style>{`
          /* Mobile-only upload copy (drag-drop doesn't exist on touch).
             Desktop-only spans swap to the drag-aware variant. */
          .crayo-upload-mobile { display: none; }
          @media (max-width: 600px) {
            .crayo-upload-desktop { display: none; }
            .crayo-upload-mobile { display: inline; }
            .crayo-camera-mobile { display: flex; }
          }
          @media (min-width: 601px) {
            /* The mobile-camera button is redundant on desktop (capture is
               ignored, the input acts as a regular file picker — same as
               the drop zone above). Hide entirely. */
            .crayo-camera-mobile { display: none !important; }
          }
        `}</style>
      </section>
    </Page>
  );
}

// --- /preview/:sessionId ---
// Polls GET /api/session/:sid every 2s until `ready`. Shows the first
// previewUrl as the "free preview" hero image; the rest stay blurred +
// locked behind the CTA. The CTA calls POST /api/session/:sid/checkout
// and redirects the browser to Stripe Checkout's hosted URL.
function PreviewPage({ sessionId }) {
  const toast = useToast();
  const [session, setSessionState] = useStateC(null);
  const [loadError, setLoadError] = useStateC(null);
  const [starting, setStarting] = useStateC(false);

  // Poll loop. Keeps a single AbortController per tick so navigating away
  // (component unmount) drops the in-flight request and prevents state
  // updates on unmounted components.
  useEffectC(() => {
    let alive = true;
    let abort = null;
    let timeoutId = null;

    const tick = async () => {
      if (!alive) return;
      abort = new AbortController();
      try {
        const s = await getSession(sessionId, { signal: abort.signal });
        if (!alive) return;
        setSessionState(s);
        // Pre-payment we only generate the FIRST photo. Stop polling once
        // photo 0 reaches a terminal state (done or failed); the rest stay
        // pending until the Stripe webhook fans them out post-payment.
        const first = s && s.photos && s.photos[0];
        const firstTerminal = first && (first.status === 'done' || first.status === 'failed');
        if (firstTerminal || (s && (s.status === 'failed' || s.status === 'expired'))) {
          return;
        }
      } catch (err) {
        if (!alive) return;
        if (err && err.name === 'AbortError') return;
        // Session not found -> 404 case; surface a specific message.
        if (err && err.code === 'session.not_found') {
          setLoadError(err.message || 'Sesiunea nu a fost găsită.');
          return;
        }
        // Transient network error: keep polling. Cap the log noise.
        // eslint-disable-next-line no-console
        console.warn('[crayo preview] poll error', err);
      }
      timeoutId = setTimeout(tick, 2000);
    };
    tick();

    return () => {
      alive = false;
      if (abort) abort.abort();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [sessionId]);

  const toCheckout = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const { checkoutUrl } = await startCheckout(sessionId);
      if (!checkoutUrl) throw new ApiError('checkout.bad_response', 'Stripe nu a răspuns. Reîncearcă.');
      // Full-page redirect, not hash nav. Stripe takes over from here.
      window.location.assign(checkoutUrl);
    } catch (err) {
      toast.push((err && err.message) || 'Nu am putut porni plata. Reîncearcă într-un minut.');
      setStarting(false);
    }
  };

  if (loadError) {
    return (
      <Page>
        <section className="container-narrow" style={{ textAlign: 'center', paddingTop: 80 }}>
          <h1>Sesiunea nu a fost găsită</h1>
          <p className="muted" style={{ marginTop: 12 }}>{loadError}</p>
          <a href="#/create" onClick={(e)=>{e.preventDefault();navigate('/create');}} className="btn btn-primary" style={{ marginTop: 24 }}>
            Reîncarcă pozele
          </a>
        </section>
      </Page>
    );
  }

  // Pre-payment we only generate the first photo. Show the overlay until
  // photo[0] is terminal (done or failed). The remaining photos stay
  // 'pending' here and are fanned out post-payment by the Stripe webhook.
  const photos = (session && session.photos) || [];
  const firstPhoto = photos[0];
  const firstTerminal = firstPhoto && (firstPhoto.status === 'done' || firstPhoto.status === 'failed');
  if (!session || !firstTerminal) {
    return <GeneratingOverlay session={session} />;
  }

  // Page count = number of photos the user actually uploaded. The legacy
  // `session.tier` is a cap (equals MAX_PHOTOS on the flat offer; currently
  // 20), not the count, so reading from it produces a misleading number
  // when the user uploaded fewer photos than the cap.
  const n = photos.length || session.tier;
  const price = priceForPages(n);
  const firstDone = (firstPhoto.status === 'done' && firstPhoto.previewUrl) ? firstPhoto : photos[0];

  return (
    <Page>
      <section style={{ paddingTop: 48, paddingBottom: 64 }}>
        <div className="container-narrow">
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div className="eyebrow">Previzualizare</div>
            <h1 style={{ marginBottom: 10 }}>Prima ta pagină e gata!</h1>
            <p style={{ color: 'var(--muted)', fontSize: 16 }}>Așa arată pagina 1 din cartea ta. Celelalte {Math.max(0, n - 1)} se deblochează după plată.</p>
          </div>

          {/* Main preview, real generated image */}
          <div className="card fade-in" style={{ padding: 16, marginBottom: 20 }}>
            <div style={{ aspectRatio: '4/3', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--soft)', background: 'var(--paper)' }}>
              {firstDone && firstDone.previewUrl ? (
                <img
                  src={firstDone.previewUrl}
                  alt={`Pagina 01 din ${String(n).padStart(2,'0')}`}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', background: 'var(--paper)' }}
                />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', color: 'var(--muted)', fontSize: 14 }}>
                  Prima pagină nu a putut fi generată. Te rugăm să reîncarci pozele sau să ne scrii la salut@crayo.ro.
                </div>
              )}
            </div>
          </div>

          {/* Locked grid for the rest */}
          {n > 1 && (
            <>
              <div style={{ fontSize: 13, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600, marginBottom: 16 }}>
                Celelalte pagini
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12, marginBottom: 40 }}>
                {Array.from({ length: n - 1 }).map((_, i) => {
                  const p = photos[i + 1];
                  return (
                    <div key={i} style={{ position: 'relative', aspectRatio: '4/3', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--soft)', background: 'var(--paper)' }}>
                      {p && p.previewUrl ? (
                        <div style={{ filter: 'blur(10px)', width: '100%', height: '100%' }}>
                          <img src={p.previewUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      ) : (
                        <div style={{ width: '100%', height: '100%', background: 'var(--soft)' }} />
                      )}
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(253,249,243,0.55)', display: 'grid', placeItems: 'center' }}>
                        <div style={{ textAlign: 'center' }}>
                          <Icon.Lock size={22} />
                          <div style={{ fontSize: 11, marginTop: 6, fontWeight: 500, letterSpacing: '0.05em' }}>Pagina {String(i+2).padStart(2,'0')}</div>
                        </div>
                      </div>
                      <div style={{ position: 'absolute', top: '50%', left: '-20%', right: '-20%', transform: 'rotate(-20deg)', textAlign: 'center', fontSize: 10, letterSpacing: '0.2em', color: 'rgba(198,93,59,0.6)', textTransform: 'uppercase', fontWeight: 600 }}>
                        Plătit, se deblochează
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Summary */}
          <div className="card" style={{ marginBottom: 20, padding: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
              <div>
                <div style={{ fontFamily: 'Fraunces, serif', fontSize: 22, fontWeight: 500, marginBottom: 4 }}>Cartea ta</div>
                <div style={{ fontSize: 14, color: 'var(--muted)' }}>{n} {n === 1 ? 'pagină' : n >= 20 ? 'de pagini' : 'pagini'} · PDF printabil · livrare pe email în ~2 min</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Fraunces, serif', fontSize: 16, fontWeight: 400, color: 'var(--muted)', textDecoration: 'line-through', lineHeight: 1, marginBottom: 2 }}>
                  19,90 lei
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, justifyContent: 'flex-end' }}>
                  <div style={{ fontFamily: 'Fraunces, serif', fontSize: 32, fontWeight: 500 }}>
                    9,90 lei
                  </div>
                  <div style={{ display: 'inline-block', padding: '2px 8px', background: 'var(--terracotta)', color: 'var(--paper)', borderRadius: 999, fontSize: 11, fontWeight: 600, letterSpacing: '0.04em' }}>
                    -50%
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Saving callout — reinforces the discount at moment of decision */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10, color: 'var(--terracotta)', fontSize: 13, fontWeight: 600 }}>
            Economisești 10 lei (-50%)
          </div>
          {/* CTA, real Stripe Checkout redirect */}
          <button onClick={toCheckout} disabled={starting} className="btn btn-primary btn-lg btn-full">
            {starting ? 'Pregătim plata...' : 'Cumpără PDF · 9,90 lei'}
          </button>
          {/* Refund guarantee, immediately under the CTA so the user sees
              the safety net at the moment of decision. */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12, color: 'var(--muted)', fontSize: 13 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon.Check /> Garanție 100% — banii înapoi dacă nu îți place rezultatul.</span>
          </div>

          {/* Trust row */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 28, marginTop: 20, flexWrap: 'wrap', color: 'var(--muted)', fontSize: 13 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon.Shield /> Plată securizată prin Stripe</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon.Infinity /> Fără abonament</span>
          </div>
        </div>
      </section>
    </Page>
  );
}

// Overlay shown pre-payment while the backend generates the FIRST photo as
// a watermarked preview. The remaining tier-many photos stay 'pending' and
// are fanned out post-payment by the Stripe webhook (so we don't pay for
// generation on visitors who never check out).
function GeneratingOverlay({ session }) {
  const firstStatus = session && session.photos && session.photos[0] && session.photos[0].status;
  const statusText = (() => {
    if (!session) return 'Analizăm fotografia...';
    if (!firstStatus || firstStatus === 'pending') return 'Pregătim prima ta pagină...';
    if (firstStatus === 'generating') return 'Schițăm contururile...';
    if (firstStatus === 'failed') return 'Aproape gata...';
    return 'Aproape gata...';
  })();

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--paper)', zIndex: 999, display: 'grid', placeItems: 'center', padding: 24 }}>
      <div style={{ textAlign: 'center', maxWidth: 440 }}>
        {/* Hand-drawn pencil that "draws" a face */}
        <svg viewBox="0 0 200 160" width="200" height="160" fill="none" style={{ margin: '0 auto', display: 'block' }}>
          <g stroke="#1A1A1A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle className="pencil-draw-circle" cx="100" cy="80" r="44" pathLength="100" />
            <path className="pencil-draw-eye-l" d="M 84 72 Q 86 68 88 72" pathLength="100" />
            <path className="pencil-draw-eye-r" d="M 112 72 Q 114 68 116 72" pathLength="100" />
            <path className="pencil-draw-smile" d="M 86 94 Q 100 104 114 94" pathLength="100" />
          </g>
          <g className="pencil-anim">
            <path d="M 0 0 L 26 -8 L 32 -2 L 6 6 Z" transform="translate(0, 0)" stroke="#1A1A1A" strokeWidth="1.4" strokeLinejoin="round" fill="var(--paper)" />
            <path d="M 26 -8 L 34 -10 L 40 -4 L 32 -2" stroke="#1A1A1A" strokeWidth="1.4" strokeLinejoin="round" fill="var(--paper)" />
            <path d="M -1 3 L -8 8 L 6 6" stroke="#1A1A1A" strokeWidth="1.4" strokeLinejoin="round" fill="#C65D3B" opacity="0.9" />
          </g>
        </svg>

        <style>{`
          .pencil-draw-circle { stroke-dasharray: 100; stroke-dashoffset: 100; animation: pd-circle 30s linear forwards; }
          .pencil-draw-eye-l, .pencil-draw-eye-r, .pencil-draw-smile { stroke-dasharray: 100; stroke-dashoffset: 100; animation: pd-line 30s linear forwards; }
          @keyframes pd-circle { from { stroke-dashoffset: 100; } 40% { stroke-dashoffset: 0; } to { stroke-dashoffset: 0; } }
          @keyframes pd-line { 0% { stroke-dashoffset: 100; } 40% { stroke-dashoffset: 100; } 100% { stroke-dashoffset: 0; } }
          .pencil-anim { animation: pencil-move 4s ease-in-out infinite; transform-origin: center; }
          @keyframes pencil-move {
            0%   { transform: translate(60px, 50px) rotate(-28deg); }
            25%  { transform: translate(130px, 72px) rotate(-10deg); }
            50%  { transform: translate(110px, 110px) rotate(25deg); }
            75%  { transform: translate(70px, 100px) rotate(-5deg); }
            100% { transform: translate(60px, 50px) rotate(-28deg); }
          }
        `}</style>

        <h2 style={{ fontSize: 26, marginTop: 20, marginBottom: 14 }}>
          Îți desenăm cartea...
        </h2>
        <p className="fade-in" style={{ color: 'var(--muted)', fontSize: 16, minHeight: 28 }}>
          {statusText}
        </p>
        <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 40, letterSpacing: '0.05em' }}>
          Durează câteva minute.
        </p>
      </div>
    </div>
  );
}

Object.assign(window, { CreatePage, PreviewPage, priceForPages, formatPrice });
